import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type PartnerService from 'tio-employee/services/partner';

/** Query params for the upload wizard; an object type so it fits a link's `@query`. */
export type UploadIntent = {
  /** The product the upload serves: `slr`, `pslf`, `syf` or `dashboard`. */
  source: string;
  /** Comma-separated document types the wizard offers, best option first. */
  providers: string;
};

/** Whether any active employment carries a plan that pays contributions. */
function isSlrMember(person: PersonModel): boolean {
  return person.employees.some(
    (employee) => employee.isActive && !!employee.plan && employee.plan.cadence !== 'NEVER'
  );
}

/** Whether the only active plan is a wellness plan, one that never pays. */
function isWellnessOnly(person: PersonModel): boolean {
  return (
    person.employees.some(
      (employee) => employee.isActive && !!employee.plan && employee.plan.cadence === 'NEVER'
    ) && !isSlrMember(person)
  );
}

/**
 * The query params the upload wizard needs, derived from the person's plans
 * and the partner's products the way the account-activity page always did.
 *
 * @param person the person uploading, with `employees.plan` loaded
 * @param partner the partner service, for PSLF and SYF enablement
 * @returns the wizard's `source` and `providers` params
 */
export function uploadIntentFor(person: PersonModel, partner: PartnerService): UploadIntent {
  const slr = isSlrMember(person);
  const pslf = Boolean(partner.isPslfEnabled);
  const syf = Boolean(partner.isSyfEnabled);
  const pslfOnly = pslf && !slr && !syf;

  let source = 'dashboard';
  if (slr) source = 'slr';
  else if (pslf) source = 'pslf';
  else if (syf) source = 'syf';

  const providers = pslfOnly || isWellnessOnly(person) ? 'nslds,statement' : 'statement,nslds';

  return { source, providers };
}
